<script lang="ts">
	import Carousel from '$lib/carousel.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import { fitText } from '$lib/actions/fitText';
	import type { SbImage } from '$lib/storyblok/types';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { fly, type TransitionConfig } from 'svelte/transition';
	import TimedGalleryIndicator from '$lib/timedGalleryIndicator.svelte';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import type { EmblaCarouselType } from 'embla-carousel';
	import { checkVisibility } from '$lib/actions/checkVisibility';
	import { scrollPosition } from '$lib/actions/scrollPosition';
	import { convertRange } from '$lib/utils/number';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import Blok from '$lib/storyblok/blok.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import { innerWidth } from 'svelte/reactivity/window';
	import type { QuoteCarouselItem } from './quoteCarouselItem.svelte';

	interface Props {
		content: ISbStoryData<{
			alignment?: 'left' | 'center';
			headline: string;
			subline: string;
			items: Array<ISbStoryData<QuoteCarouselItem>['content']>;
		}>['content'];
	}

	let { content }: Props = $props();

	let currentIndex = $state(0);
	let prevIndex = $state(0);

	let currentItem = $derived(content.items[currentIndex]);

	let flyDir: 1 | -1 = 1;

	$effect(() => {
		if (prevIndex !== currentIndex) {
			if (prevIndex < currentIndex) {
				flyDir = 1;
			} else {
				flyDir = -1;
			}

			prevIndex = currentIndex;
		}
	});

	let flyConfig: TransitionConfig = $derived({
		duration: 600,
		x: content.alignment !== 'center' ? flyDir * 30 : undefined,
		y: content.alignment === 'center' ? flyDir * 30 : undefined
	});

	let bgColor = $derived(content.items[currentIndex]?.bg_color || '#FFFFFF');

	let gridWidth = $state(1400);
	let bgContainer = $state<HTMLDivElement>();

	const autoplayDuration = 5000;

	let emblaMobile = $state<EmblaCarouselType>();
	let emblaDesktop = $state<EmblaCarouselType>();

	let offsetPercentage = $state(0);

	let width = $derived(innerWidth.current ?? 1024);
	let fullScale = $derived(Math.ceil(1 + (width - gridWidth) / width));

	let scale = $derived(
		content.alignment === 'center'
			? fullScale
			: convertRange(100 - Math.abs(offsetPercentage), 20, 100, 1, fullScale)
	);
	let borderRadius = $derived(
		content.alignment === 'center' ? 0 : convertRange(fullScale - scale, 0.9, 1, 0, 24)
	);

	let autoplay = $state(false);

	let imgSize = $derived(content.alignment === 'center' ? 200 : 700);
</script>

<section
	use:editable={content}
	use:checkVisibility={{ threshold: 0.33 }}
	onvisible={({ detail }) => (autoplay = detail)}
>
	{#if content.headline.length > 0 || content.subline.length > 0}
		<PageGrid16>
			<div
				class="col-span-full mb-6 text-balance text-center text-brown-1 max-lg:mx-5 lg:col-start-3 lg:col-end-[15] lg:mb-15"
			>
				{#if content.headline.length > 0}
					<h1
						class="mb-3 text-[36px]/[112%] font-semibold tracking-[-.36px] lg:text-[48px]/[120%] lg:tracking-[-.48px]"
					>
						{content.headline}
					</h1>
				{/if}
				{#if content.subline.length > 0}
					<div
						class="text-[16px]/[150%] tracking-[-.16px] lg:mt-6 lg:text-[20px]/[140%] lg:tracking-[-.2px]"
					>
						<SbRichtext text={content.subline} />
					</div>
				{/if}
			</div>
		</PageGrid16>
	{/if}

	<div
		class={[
			'relative bg-white',
			{ 'lg:pb-22': content.alignment === 'center', 'lg:pb-32': content.alignment !== 'center' }
		]}
		use:scrollPosition
		onchangescroll={({ detail }) => (offsetPercentage = detail.offsetPercentage)}
	>
		<Carousel
			bind:currentIndex
			bind:embla={emblaMobile}
			containerClass="lg:hidden"
			class="flex"
			{autoplay}
			{autoplayDuration}
		>
			{#each content.items as item}
				<Blok content={item} alignment={content.alignment} />
			{/each}
		</Carousel>

		<PageGrid16 class="absolute inset-0 max-lg:hidden" bind:gridWidth>
			<div
				bind:this={bgContainer}
				class="absolute inset-0 z-0 col-span-full transition-colors duration-1000 ease-in-out"
				style="background-color: {bgColor}; transform: scaleX({scale}); border-radius: {borderRadius}px"
			></div>
		</PageGrid16>

		{#if currentItem}
			<PageGrid16
				class={[
					'relative max-lg:hidden',
					{
						'pt-22': content.alignment === 'center',
						'pb-16 pt-48': content.alignment !== 'center'
					}
				]}
			>
				<div
					class={[
						'col-span-full',
						{
							'row-start-2 text-center lg:col-start-5 lg:col-end-13':
								content.alignment === 'center',
							'lg:col-start-2 lg:col-end-9': content.alignment !== 'center'
						}
					]}
				>
					<div
						class={[
							'relative select-none',
							{
								'mb-[120px] h-full max-h-[204px] lg:max-h-[314px]': content.alignment !== 'center',
								'mb-8 h-18 lg:h-34': content.alignment === 'center'
							}
						]}
					>
						{#key currentItem.quote}
							<div
								class="absolute text-balance leading-[140%]"
								use:fitText={{ minFontSize: content.alignment === 'center' ? 32 : 18 }}
								transition:fly={flyConfig}
							>
								<SbRichtext text={currentItem.quote} />
							</div>
						{/key}
					</div>

					<div class={['relative', { 'h-13': content.alignment === 'center' }]}>
						{#key currentItem.author}
							<div
								class={[
									'absolute flex select-none flex-col',
									{
										'w-full': content.alignment === 'center',
										'gap-y-1': content.alignment !== 'center'
									}
								]}
								transition:fly={flyConfig}
							>
								<div
									class={[
										'text-brown-1',
										{
											'font-serif text-[20px]/[124%] tracking-[-.4px] lg:text-[24px]/[120%] lg:tracking-[-.48px]':
												content.alignment !== 'center',
											'text-[14px]/[160%] lg:text-[20px]/[140%] lg:tracking-[-.2px]':
												content.alignment === 'center'
										}
									]}
								>
									{currentItem.author}
								</div>
								<div
									class={[
										'text-brown-1/40',
										{
											'text-[14px]/[112%] max-lg:tracking-[-.14px] lg:text-[16px]/[150%]':
												content.alignment !== 'center',
											'text-[12px]/[160%] lg:text-[14px]/[160%]': content.alignment === 'center'
										}
									]}
								>
									{currentItem.accolades}
								</div>
							</div>
						{/key}
					</div>
				</div>
				<div
					class={[
						'col-span-full',
						{
							'col-start-7 mx-auto lg:col-end-11': content.alignment === 'center',
							'lg:col-end-16 lg:col-start-10': content.alignment !== 'center'
						}
					]}
				>
					<Carousel
						bind:embla={emblaDesktop}
						bind:currentIndex
						containerClass={[
							'z-10 relative rounded-2xl',
							{
								'w-[120px] h-[120px] lg:w-[200px] lg:h-[200px] aspect-square overflow-hidden mb-8':
									content.alignment === 'center'
							}
						]}
						class="lg:flex"
						draggable={false}
						autoplay={content.items.length > 1}
						{autoplayDuration}
					>
						{#each content.items as item, i}
							<div class="aspect-square flex-none basis-full">
								<Image
									src={item.image.filename}
									alt={item.image.alt}
									title={item.image.title}
									class={[
										'h-full w-full select-none object-cover',
										{ 'rounded-l-2xl': i === 0, 'rounded-r-2xl': i === content.items.length - 1 }
									]}
									height={imgSize}
									width={imgSize}
									sizes={{ 1024: { height: imgSize * 2 } }}
								/>
							</div>
						{/each}
					</Carousel>
				</div>
			</PageGrid16>

			{#if content.items.length > 1}
				{#if emblaMobile}
					<div class="absolute inset-x-0 bottom-0 flex px-5 pb-8 lg:hidden">
						<TimedGalleryIndicator
							embla={emblaMobile}
							count={content.items.length}
							active={currentIndex}
						/>
					</div>
				{/if}

				<div class="flex justify-center max-lg:hidden">
					<div class="flex items-center justify-between gap-x-6">
						<Button
							title="Previous quote"
							class="btn-white"
							onclick={() => {
								if (!emblaDesktop) return;

								if (emblaDesktop.canScrollPrev()) {
									emblaDesktop.scrollPrev();
								} else {
									emblaDesktop.scrollTo(content.items.length - 1);
								}
							}}><ArrowLeft /></Button
						>
						{#if emblaDesktop}
							<TimedGalleryIndicator
								embla={emblaDesktop}
								count={content.items.length}
								active={currentIndex}
							/>
						{/if}
						<Button
							title="Next quote"
							class="btn-white"
							onclick={() => {
								if (!emblaDesktop) return;

								if (emblaDesktop.canScrollNext()) {
									emblaDesktop.scrollNext();
								} else {
									emblaDesktop.scrollTo(0);
								}
							}}><ArrowRight /></Button
						>
					</div>
				</div>
			{/if}
		{/if}
	</div>
</section>
